import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { serializeParams } from './paramsSerializer';
import useTokenStatus from './useTokenStatus';
import ErrorResponseCodes from './errorResponseCodes';
import ErrorModel from './models/ErrorModel';
import getSelectedLocaleFromLocalStorage from '@/helpers/i18n/localeStorage';
import { resetStorageAndGlobalStateAuth, resetStorageAndGlobalStatePermissions } from './useAuthStatus';

export const createHttpClient = function (versionBase?: string, headers?: Record<string, string>): AxiosInstance {
  const cleanVersionBase = versionBase ? versionBase.replace(/^\/+|\/+$/g, '') : 'v1';

  // We assume here that the default base url always contain the '/v1' in it.
  let versionedBaseUrl = process.env.VUE_APP_PARTNER_DASHBOARD_ENVIRONMENT_URL;
  if (versionBase != 'v1') {
    versionedBaseUrl = versionedBaseUrl.replace('/v1', `/${cleanVersionBase}`);
  }

  const clientInstance: AxiosInstance = axios.create({
    baseURL: versionedBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Accept-Language': getSelectedLocaleFromLocalStorage(),
    },
    responseType: 'json',
    paramsSerializer: serializeParams,
  });

  clientInstance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      const { partnerToken, partnerTokenValid } = useTokenStatus();
      if (partnerTokenValid && !!partnerToken) {
        config.headers = {
          ...config.headers,
          'x-partner-token': partnerToken,
        };
      }
      if (headers) {
        config.headers = {
          ...config.headers,
          ...headers,
        };
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(new ErrorModel(error.response?.data)),
  );

  clientInstance.interceptors.response.use(
    (res) => Promise.resolve(res.data),
    (error: AxiosError) => {
      // Partner token invalid/expired. The platform error envelope wraps
      // the code under `message.code`, not on `message` itself — the old
      // comparison was against the whole object and could never match.
      const responseData = error.response?.data as { message?: { code?: string } } | undefined;
      if (
        error.response?.status === 401 &&
        responseData?.message?.code === ErrorResponseCodes.ERROR_SESSION_INVALID
      ) {
        resetStorageAndGlobalStateAuth();
        resetStorageAndGlobalStatePermissions();
      }
      return Promise.reject(new ErrorModel(error.response?.data));
    },
  );
  return clientInstance;
};

const httpClient = createHttpClient();

export default httpClient;
